import { useEffect, useRef, useState } from 'react';
import {
  PRICING_TIERS, LAUNCH_PRICE_ENDS_AT, BUNDLES, isBundleRelevantForFeatures,
  type PlanType, type BundleId,
} from '../../shared/types';
import TypologyShape, { type ShapeName } from './ui/TypologyShape';
import { useAuth } from '../contexts/AuthContext';
import { createCheckoutSession, createBundleCheckout, openBillingPortal } from '../services/api';

// Bêta privée : Stripe est débranché (les routes /api/stripe répondent 503
// billing_disabled). Les CTA d'achat sont remplacés par une demande d'accès
// bêta par email ; les tarifs restent affichés à titre indicatif.
const BILLING_DISABLED = true;

// Dérivé de PRICING_TIERS, jamais saisi en dur : le jour où un tarif revient
// dans shared/types.ts, le sélecteur mensuel/annuel et les mentions de prix
// se réaffichent sans qu'on ait à y repenser ici.
const ALL_ON_QUOTE = PRICING_TIERS.every(
  (t) => t.priceMonthly === null && t.priceYearly === null,
);
const BETA_CONTACT_MAILTO =
  'mailto:contact@visaly.fr?subject=' + encodeURIComponent('Demande d\'accès bêta — Visaly');

function formatPrice(cents: number | null): string {
  if (cents === null) return 'Sur devis';
  if (cents === 0) return '0€';
  return `${(cents / 100).toFixed(0)}€`;
}

function formatLaunchEndDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
}

interface PricingPageProps {
  onClose?: () => void;
  scrollTo?: string;
}

/* Teintes des cartes de forfait. L'offre mise en avant sort du nuancier :
 * elle passe en encre, seule couleur qui tranche vraiment sur le crème. */
// Le premier rang est consommé par l'offre mise en avant (qui passe en encre) :
// on démarre donc le nuancier au second, sinon le forfait suivant héritait du
// crème et se fondait dans le fond de page.
const TIER_TINTS = ['tint-sun', 'tint-sky', 'tint-khaki', 'tint-neutral'];

/* Silhouette en filigrane par palier — même vocabulaire que les typologies
   d'analyse (ui/TypologyShape), sans lien sémantique : ici c'est un motif
   de signature, pas un repère. */
const TIER_SHAPES: ShapeName[] = ['bouclier', 'croix', 'hexagone', 'blob'];

export default function PricingPage({ onClose, scrollTo }: PricingPageProps) {
  const bundlesRef = useRef<HTMLDivElement>(null);
  const { user, organization, features } = useAuth();

  useEffect(() => {
    if (scrollTo === 'bundles' && bundlesRef.current) {
      const timer = setTimeout(() => {
        bundlesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [scrollTo]);
  const [interval, setInterval] = useState<'month' | 'year'>('month');
  const [loadingPlan, setLoadingPlan] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const currentPlan = organization?.plan ?? user?.plan ?? 'free';

  // Bundles pertinents pour le plan courant : on masque ceux dont TOUS les crédits
  // sont déjà couverts par l'abonnement (Premium → aucun ; Pro → social/événement/campagne ;
  // sans abo → tous). Évite de vendre des crédits redondants.
  const relevantBundles = BUNDLES
    .filter((b) => isBundleRelevantForFeatures(b, features))
    .sort((a, b) => a.priceCents - b.priceCents);
  // Masqués tant que la facturation est débranchée : ils affichent des montants
  // alors qu'aucun achat n'aboutit (/api/stripe répond 503 billing_disabled),
  // et un prix de bundle ancrerait la discussion tarifaire aussi sûrement
  // qu'un prix d'abonnement.
  const hasBundles = !BILLING_DISABLED && relevantBundles.length > 0;
  const hasActiveSub = !!organization?.stripeSubscriptionId;

  async function handleSubscribe(plan: PlanType) {
    if (BILLING_DISABLED) {
      window.location.href = BETA_CONTACT_MAILTO;
      return;
    }
    if (plan === 'enterprise') return;
    if (!user) {
      setError('Vous devez être connecté pour souscrire à un abonnement.');
      return;
    }
    setError(null);
    setLoadingPlan(plan);
    try {
      const { url } = await createCheckoutSession(plan as 'pro' | 'premium', interval);
      if (!url) throw new Error('URL de paiement manquante.');
      window.location.href = url;
    } catch (err) {
      console.error('Checkout error:', err);
      setError(
        err instanceof Error
          ? `Impossible d'ouvrir la page de paiement : ${err.message}`
          : "Impossible d'ouvrir la page de paiement.",
      );
      setLoadingPlan(null);
    }
  }

  async function handleBundle(bundleId: BundleId) {
    if (BILLING_DISABLED) {
      window.location.href = BETA_CONTACT_MAILTO;
      return;
    }
    if (!user) {
      setError('Vous devez être connecté pour acheter un bundle.');
      return;
    }
    setError(null);
    setLoadingPlan(`bundle:${bundleId}`);
    try {
      const { url } = await createBundleCheckout(bundleId);
      if (!url) throw new Error('URL de paiement manquante.');
      window.location.href = url;
    } catch (err) {
      console.error('Bundle checkout error:', err);
      setError(
        err instanceof Error
          ? `Impossible d'ouvrir la page de paiement : ${err.message}`
          : "Impossible d'ouvrir la page de paiement.",
      );
      setLoadingPlan(null);
    }
  }

  // Abonné existant : on route les changements d'offre vers le portail Stripe
  // (changement proraté, JAMAIS un 2e abonnement créé en parallèle).
  async function handlePortal() {
    if (BILLING_DISABLED) {
      window.location.href = BETA_CONTACT_MAILTO;
      return;
    }
    setError(null);
    setLoadingPlan('portal');
    try {
      const { url } = await openBillingPortal();
      if (!url) throw new Error('URL du portail manquante.');
      window.location.href = url;
    } catch {
      setError("Impossible d'ouvrir le portail de facturation.");
      setLoadingPlan(null);
    }
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-8 animate-fade-in">
      {/* Back button */}
      {onClose && (
        <div className="mb-4">
          <button
            onClick={onClose}
            className="inline-flex items-center gap-2 rounded-full bg-ink px-4 py-2 text-sm text-white transition-transform hover:scale-[1.03]"
            aria-label="Retour à l'accueil"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.6} d="M15 19l-7-7 7-7" />
            </svg>
            Retour
          </button>
        </div>
      )}

      {/* Header */}
      <div className="text-center mb-10">
        {/* Bandeau bêta privée — Stripe débranché, prix indicatifs */}
        {BILLING_DISABLED && (
          <div className="max-w-2xl mx-auto mb-6 rounded-3xl bg-rose-pastel-200 p-5 text-left">
            <p className="mb-1 text-sm font-semibold text-ink">
              Bêta privée — la facturation n'est pas encore activée.
            </p>
            <p className="text-xs leading-relaxed text-ink/70">
              L'accès se fait sur invitation, avec un pool d'analyses offert.{' '}
              {ALL_ON_QUOTE
                ? 'La tarification est établie sur devis, selon le volume et le périmètre.'
                : 'Les tarifs ci-dessous sont indicatifs.'}{' '}
              <a href={BETA_CONTACT_MAILTO} className="font-medium underline transition-colors hover:text-ink">
                Demander un accès bêta →
              </a>
            </p>
          </div>
        )}
        <div className="inline-flex items-center gap-2 px-3 py-1 mb-4 rounded-full bg-sun-400">
          <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-ink" />
          <span className="text-xs font-medium text-ink">
            {ALL_ON_QUOTE
              ? 'Tarification sur devis'
              : BILLING_DISABLED
                ? "Tarifs indicatifs — prix de lancement prévus jusqu'au " + formatLaunchEndDate(LAUNCH_PRICE_ENDS_AT)
                : "Prix de lancement Founding Member jusqu'au " + formatLaunchEndDate(LAUNCH_PRICE_ENDS_AT)}
          </span>
        </div>
        <h1 className="font-display mb-3 text-[2.15rem] leading-[1.08] text-slate-900 md:text-[2.7rem]">
          Choisissez votre abonnement
        </h1>
        <p className="text-slate-600 max-w-xl mx-auto">
          {ALL_ON_QUOTE
            ? 'Chaque périmètre est différent : le tarif est établi sur devis, selon le volume de supports et les régimes produits concernés.'
            : "Moins cher qu'une heure d'avocat. Plus rapide que n'importe quel cabinet."}
        </p>
        {hasBundles && (
        <p className="mt-3 text-sm text-slate-500 max-w-xl mx-auto">
          Besoin ponctuel ?{' '}
          <button
            type="button"
            onClick={() => bundlesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
            className="underline underline-offset-2 transition-colors hover:text-ink"
          >
            Découvrez les bundles thématiques ci-dessous
          </button>
        </p>
        )}

        {/* Interval toggle — masqué quand tout est sur devis : il basculerait
            entre « Sur devis » et « Sur devis ». */}
        {!ALL_ON_QUOTE && (
        <div className="flex items-center justify-center gap-3 mt-6">
          <button
            onClick={() => setInterval('month')}
            className={`px-4 py-1.5 text-sm rounded-full transition-colors ${
              interval === 'month'
                ? 'bg-ink text-white'
                : 'bg-cream-card text-slate-600 hover:bg-ink/8'
            }`}
          >
            Mensuel
          </button>
          <button
            onClick={() => setInterval('year')}
            className={`px-4 py-1.5 text-sm rounded-full transition-colors ${
              interval === 'year'
                ? 'bg-ink text-white'
                : 'bg-cream-card text-slate-600 hover:bg-ink/8'
            }`}
          >
            Annuel
            <span className="ml-1.5 text-xs text-emerald-500 font-medium">-20%</span>
          </button>
        </div>
        )}
      </div>

      {/* Error banner */}
      {error && (
        <div className="max-w-2xl mx-auto mb-6 rounded-2xl bg-red-100 p-3.5 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Pricing cards */}
      <div className="grid md:grid-cols-3 gap-5 mb-16 max-w-5xl mx-auto">
        {PRICING_TIERS.map((tier, i) => {
          const price = interval === 'year' ? tier.priceYearly : tier.priceMonthly;
          const regularPrice = interval === 'year' ? tier.priceYearlyRegular : tier.priceMonthlyRegular;
          const showLaunchPrice = tier.launchPrice === true && regularPrice != null && price != null && regularPrice > price;
          const isCurrentPlan = tier.plan === currentPlan;
          const isHighlighted = tier.highlighted;
          const isLoading = loadingPlan === tier.plan;

          return (
            <div
              key={tier.plan}
              className={`relative isolate flex flex-col overflow-hidden rounded-3xl p-6 transition-all ${
                isHighlighted ? 'tint-ink scale-[1.03]' : TIER_TINTS[i % TIER_TINTS.length]
              }`}
            >
              {/* Filigrane : une silhouette surdimensionnée qui déborde du coin.
                  Elle donne à chaque palier une signature sans un mot de plus.
                  `isolate` sur la carte + `-z-10` ici : le motif se glisse entre
                  l'aplat de la carte et son contenu, sans jamais le recouvrir. */}
              <span
                className={`pointer-events-none absolute -bottom-12 -right-10 -z-10 block h-40 w-40 ${
                  isHighlighted ? 'text-white/15' : 'text-ink/[0.07]'
                }`}
                aria-hidden
              >
                <TypologyShape shape={TIER_SHAPES[i % TIER_SHAPES.length]} color="currentColor" />
              </span>

              {isHighlighted && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-sun-400 px-3 py-1 text-xs font-bold text-ink">
                  RECOMMANDÉ
                </div>
              )}

              <h3 className={`text-lg font-semibold mb-1 ${
                isHighlighted ? 'text-white' : 'text-ink'
              }`}>
                {tier.name}
              </h3>
              <p className={`text-sm mb-4 ${
                isHighlighted ? 'text-white/60' : 'text-ink/60'
              }`}>
                {tier.tagline}
              </p>

              <div className="mb-5">
                {showLaunchPrice && (
                  <div className={`inline-block text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded mb-1.5 ${
                    isHighlighted
                      ? 'bg-sun-400 text-ink'
                      : 'bg-white/70 text-ink'
                  }`}>
                    Prix de lancement
                  </div>
                )}
                <div>
                  <span className={`text-3xl font-bold ${
                    isHighlighted ? 'text-white' : 'text-ink'
                  }`}>
                    {price === null && tier.priceFromLabel ? tier.priceFromLabel : formatPrice(price)}
                  </span>
                  {((price !== null && price > 0) || tier.priceFromLabel) && (
                    <span className={`text-sm ${
                      isHighlighted ? 'text-white/60' : 'text-ink/60'
                    }`}>
                      /mois
                    </span>
                  )}
                </div>
                {showLaunchPrice && regularPrice != null && (
                  // Wording carefully phrased forward-looking ("tarif normal après…")
                  // and NOT as a past reference price ("au lieu de…"). Art. L.112-1-1
                  // Code conso + Directive Omnibus : une annonce de réduction exige
                  // que le prix de référence ait été réellement pratiqué les 30
                  // jours précédents — ce qui n'est pas notre cas. Décrire une hausse
                  // future sort du périmètre de la règle.
                  <p className={`text-xs mt-1 ${
                    isHighlighted ? 'text-white/60' : 'text-ink/60'
                  }`}>
                    Tarif normal après le {formatLaunchEndDate(LAUNCH_PRICE_ENDS_AT)} : {formatPrice(regularPrice)}/mois
                  </p>
                )}
                {showLaunchPrice && tier.launchLockMonths && (
                  <p className={`text-xs mt-0.5 font-medium ${
                    isHighlighted ? 'text-khaki-300' : 'text-khaki-700'
                  }`}>
                    Tarif verrouillé {tier.launchLockMonths} mois
                  </p>
                )}
              </div>

              {/* CTA button */}
              <button
                onClick={() => {
                  if (BILLING_DISABLED) {
                    window.location.href = BETA_CONTACT_MAILTO;
                  } else if (tier.plan === 'enterprise') {
                    window.location.href = 'mailto:contact@visaly.fr?subject=Plan%20Entreprise';
                  } else if (isCurrentPlan) {
                    // déjà désactivé
                  } else if (hasActiveSub) {
                    // Abonné : changement d'offre via le portail (proraté, pas de 2e abo).
                    handlePortal();
                  } else {
                    handleSubscribe(tier.plan);
                  }
                }}
                disabled={(!BILLING_DISABLED && isCurrentPlan) || isLoading}
                className={`mb-5 w-full rounded-full py-3 text-sm font-medium transition-transform ${
                  !BILLING_DISABLED && isCurrentPlan
                    ? 'cursor-default bg-ink/10 text-ink/50'
                    : isHighlighted
                      ? 'bg-white text-ink hover:scale-[1.02]'
                      : 'bg-ink text-white hover:scale-[1.02]'
                } ${isLoading ? 'cursor-wait opacity-50' : ''}`}
              >
                {BILLING_DISABLED ? 'Demander un accès bêta' : isLoading ? 'Redirection...' : isCurrentPlan ? 'Plan actuel' : hasActiveSub ? "Gérer l'abonnement" : tier.cta}
              </button>

              {/* Features list */}
              <ul className="space-y-2 flex-1">
                {tier.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    <svg className={`w-4 h-4 mt-0.5 flex-shrink-0 ${
                      isHighlighted ? 'text-khaki-300' : 'text-khaki-600'
                    }`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.6} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className={isHighlighted ? 'text-white/80' : 'text-ink/75'}>
                      {f}
                    </span>
                  </li>
                ))}
                {tier.disabledFeatures.map((f, i) => (
                  <li key={`d-${i}`} className="flex items-start gap-2 text-sm">
                    <svg className={`w-4 h-4 mt-0.5 flex-shrink-0 ${
                      isHighlighted ? 'text-white/25' : 'text-ink/25'
                    }`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.6} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    <span className={
                      isHighlighted ? 'text-white/35' : 'text-ink/40'
                    }>
                      {f}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>

      {/* Bundles thématiques — masqués si le plan couvre déjà tout (ex. Premium) */}
      {hasBundles && (
      <div ref={bundlesRef} className="mb-12">
        <h2 className="font-display mb-2 text-center text-[1.8rem] text-slate-900">
          Bundles thématiques
        </h2>
        <p className="text-sm text-center text-slate-500 mb-2 max-w-xl mx-auto">
          Pour un projet ponctuel, sans engagement. Les crédits du bundle restent
          utilisables sans limite de temps.
        </p>
        <p className="text-xs text-center text-slate-400 mb-6 max-w-xl mx-auto">
          Un post réseau social = 1 crédit ; un carrousel en consomme plus selon sa taille
          (3 slides = 2 crédits, 7 slides = 4 crédits).
        </p>

        {/* Tous les bundles pertinents — une seule grille alignée (sans découpe Mono/Multi) */}
        <div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-5xl mx-auto">
            {relevantBundles.map((bundle) => {
              const isLoading = loadingPlan === `bundle:${bundle.id}`;
              return (
                <button
                  key={bundle.id}
                  onClick={() => handleBundle(bundle.id)}
                  disabled={loadingPlan !== null}
                  className={`glow-card tint-neutral flex flex-col rounded-3xl p-5 text-left ${
                    isLoading ? 'cursor-wait opacity-70' : loadingPlan !== null ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  <div className="flex items-baseline justify-between mb-2">
                    <h4 className="text-base font-semibold text-ink">
                      {bundle.name}
                    </h4>
                    <span className="font-display text-xl text-ink">
                      {formatPrice(bundle.priceCents)}
                    </span>
                  </div>
                  <p className="mb-3 text-xs text-ink/55">
                    {bundle.tagline}
                  </p>
                  <ul className="flex-1 space-y-1.5 text-xs text-ink/70">
                    {bundle.creditLines.map((line, i) => (
                      <li key={i} className="flex items-start gap-1.5">
                        <span className="mt-0.5 text-rose-pastel-500">•</span>
                        <span>{line.label}</span>
                      </li>
                    ))}
                  </ul>
                  {isLoading && (
                    <p className="mt-3 text-xs text-ink/60">
                      Redirection…
                    </p>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>
      )}

      {/* Value proposition footer */}
      <div className="text-center pb-4">
        {BILLING_DISABLED ? (
          <p className="text-xs text-slate-400">
            Tous les prix sont en euros HT et affichés à titre indicatif — la facturation n'est pas encore activée.
            <br />
            Accès bêta sur invitation, avec un pool d'analyses offert.
          </p>
        ) : (
          <p className="text-xs text-slate-400">
            Tous les prix sont en euros HT. Paiement sécurisé par Stripe.
            <br />
            Annulez à tout moment depuis votre espace de facturation.
            <br />
            Garantie satisfait ou remboursé 30 jours sur les abonnements, 7 jours sur les bundles.
          </p>
        )}
      </div>
    </div>
  );
}
